import { ChangeDetectorRef, Component, DoCheck, EventEmitter, Input, NgZone, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ChassisFormHandler } from '../../classes/form-handler';
import { Model } from '../../../../api/model';
import { isUndefined } from 'util';
import { Observable } from 'rxjs/Observable';
import { AuthenticationService } from '../../../../authentication/services/authentication.service';

@Component({
    selector: 'chassis-form-element',
    templateUrl: './element.component.html',
    styleUrls: ['./element.component.scss']
})
export class ElementComponent implements OnInit, DoCheck {

    /**
     * The FormGroup instance.
     */
    @Input() form: ChassisFormHandler;

    /**
     * Form layout.
     */
    @Input() layout: string;

    /**
     * The form's Model instance or data object.
     */
    @Input() model: Model | Object;

    /**
     * Complete element
     * You can insert a complete element instead of using the
     * 'type', 'id', 'name', 'attributes', 'options', 'validation' attributes
     */
    @Input() element: any;

    /**
     * Element type.
     */
    @Input() type: string;

    /**
     * Element id.
     */
    @Input() id: string;

    /**
     * Element name.
     */
    @Input() name: string;

    /**
     * Object of attributes with it's values.
     */
    @Input() attributes: Object;

    /**
     * Object of options with it's values.
     */
    @Input() options: Object;

    /**
     * Object of validations with it's values.
     */
    @Input() validation: Object;

    /**
     * Object of error returned by the backend
     */
    @Input() errors: Object;

    /**
     * Change event.
     */
    @Output() change: EventEmitter<any> = new EventEmitter();

    /**
     *
     * @type {boolean}
     */
    public conditionsPassed = true;

    /**
     * Property to store the original validation
     *
     * @type {{}}
     */
    protected originalValidation = null;

    /**
     * Constructor.
     *
     * -> do not delete translateService -> used in template
     */
    public constructor(private translate: TranslateService, private ngZone: NgZone, private authService: AuthenticationService, protected cdr: ChangeDetectorRef) {

    }

    public ngOnInit() {
        // Special rule for elements that are part of the options of another element (like options for a media upload field in entity builder)
        if (this.element.name.indexOf('.') !== -1) {
            try {
                const name                    = this.element['name'].split('.')[1];
                this.model[this.element.name] = this.model['config'][name];
            } catch (e) {
                // unknown exception -> can't do anything
            }
        }

        // When input type is checkbox we have to create an object with all the option keys
        // so we can either set the value to true or false
        if (this.isType('checkbox') && this.options && this.model[this.element.name]) {
            this.model[this.element.name] = {};
        }
    }

    /**
     * Check the conditions because the value will be changed after the Angular change trigger
     */
    ngDoCheck() {
        this.checkConditions();

        // Prevent expression has changed after it was
        // checked error.
        this.cdr.detectChanges();
    }

    /**
     * Check if the element is of the given type.
     *
     * @param type
     * @returns {any}
     */
    protected isType(type: string | Array<string>) {
        if (type instanceof Array) {
            return type['includes'](this.element['type']);
        }

        return this.element['type'] === type;
    }

    public setMediaValue(value) {
        if (!this.model[this.element.name]) {
            this.model[this.element.name] = {
                value: null,
                meta: null,
            };
        }

        this.model[this.element.name].value = value;
    }

    /**
     * Check if the user has permissions for changing the input value
     *
     * @returns {Observable<any>}
     */
    public canWrite() {

        return new Observable(observer => {
            // check if the user has permissions for write access
            if (this.element.roles_permissions && this.element.roles_permissions.length) {

                this.authService.user().subscribe(user => {

                    // get the user roles
                    const userRoles = user.roles().all().map(role => role.name);

                    let canWrite = false;

                    // check if there is a role with write permission
                    // and see if the user has that specific role
                    this.element.roles_permissions.forEach((role_permission) => {
                        if (role_permission.permission === 'read-write' && role_permission.name.indexOf(userRoles) !== -1 && !canWrite) {
                            canWrite = true;
                        }
                    });

                    observer.next(canWrite);
                    observer.complete();

                });

            } else {

                observer.next(true);
                observer.complete();

            }

        });

    }

    /**
     * Check if the form has the given layout.
     *
     * @param name
     * @returns {boolean}
     */
    protected isLayout(name: string) {
        return this.layout === name;
    }

    /**
     * Run the validator programmatically.
     * Used when the element does not update and rerun the validator automatically.
     */
    protected validate() {
        this.form.formGroup.controls[this.element.name].updateValueAndValidity();
    }

    /**
     * Make sure to two-way-bind the model.
     *
     * @param value
     */
    protected modelChanged(value) {
        this.model[this.element.name] = value;

        this.validate();

        if (this.element['translatable']) {

            // Special rule for translatable elements
            this.model[this.element['name']].ngModel = value;

        } else if (this.element.config_field !== undefined && this.element.config_field) {

            // Special rule for elements that are part of the options of another element (like options for a media upload field in entity builder)
            try {
                const name                 = this.element['name'].split('.')[1];
                this.model['config'][name] = value;
            } catch (e) {
                this.model['config'][this.element['name']] = value;
            }

        } else if (!this.isType('checkbox')) {

            // Default change rule
            this.model[this.element['name']] = value;

        } else if (this.isType('checkbox')) {

            // checkbox workaround -> checkbox value would be true or false normally
            // instead we want the object with keys and booleans for multiple checkboxes
            this.change.emit(this.model[this.element.name]);

            return true;

        }

        this.change.emit(value);
    }

    /**
     * Check all the conditions for this
     */
    protected checkConditions() {

        // Check if there are any conditions available
        if (this.element['conditions']) {

            const elements        = this.collectAllFormElements();
            let conditionalString = '';

            // Loop through the conditions
            this.element.conditions.forEach((condition) => {
                conditionalString += this.parseCondition(condition, elements);
            });

            // Check the full conditional string
            this.conditionsPassed = eval(conditionalString);

            if (!this.conditionsPassed && this.originalValidation === null) {
                // disable validation if condition not passed
                this.originalValidation = this.element.validation;
                this.element.validation = {};

            } else if (this.originalValidation !== null) {
                // restore validation if conditions passed
                this.element.validation = this.originalValidation;

            }

        } else {

            // No conditions found
            this.conditionsPassed = true;

        }
    }

    /**
     * Parses a condition group, single condition and a condition separator
     *
     * @param condition
     * @param elements
     * @returns {string}
     */
    protected parseCondition(condition, elements) {

        let conditionalString = '';

        // If it's an array, it means that it is a 'grouped' condition
        switch (condition.constructor) {

            case Array: // Grouped condition

                conditionalString += '(';
                condition.forEach((condition) => {
                    conditionalString += this.parseCondition(condition, elements);
                });
                conditionalString += ')';
                break;

            case Object: // Single condition

                // Get element
                const element = elements.filter((element) => element.name === condition.element)[0];

                // get the value and the condition value
                const value          = this.parseConditionValue(this.form.formGroup.controls[element.name].value) || undefined;
                const conditionValue = this.parseConditionValue(condition.condition);

                // Append a condition to the to be checked conditions
                conditionalString += `${conditionValue} ${condition.operator} ${value}`;
                break;

            case String: // Append condition separator

                conditionalString += this.getConditionSeparator(condition);
                break;

            default:
                console.error('Invalid DataType supplied for condition');

        }

        return conditionalString;
    }

    /**
     * Parses an actual string into a string
     *
     * @param value
     * @returns {string}
     */
    protected parseConditionValue(value) {
        return (isFinite(value) || isUndefined(value)) ? value : "'" + value + "'";
    }

    /**
     * Returns all the form elements
     *
     * @returns {any[]}
     */
    protected collectAllFormElements() {
        const elements = [];
        this.form.structure['groups'].forEach((group) => {
            group.sections.forEach((section) => {
                section.rows.forEach((row) => {
                    row.elements.forEach((element) => {
                        elements.push(element);
                    });
                })
            });
        });
        return elements;
    }

    /**
     * Parses the condition separator into a actual logical operator.
     *
     * @param separator
     * @returns {any}
     */
    protected getConditionSeparator(separator) {
        separator = separator.toLowerCase();
        switch (separator) {
            case 'and':
                return ' && ';
            case 'or':
                return ' || ';
            default:
                console.error('The second value of conditions always needs to be a AND or OR!');
                return false;
        }
    }

}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/forms/form/components/element/element.component.ts